import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { TimeRange } from "@/components/time-range-selector";

const GPU_COLORS = [
  "oklch(0.45 0.12 160)", // GPU 0 — primary green
  "oklch(0.55 0.15 250)", // GPU 1 — blue
  "oklch(0.65 0.15 85)", // GPU 2 — amber
  "oklch(0.55 0.15 300)", // GPU 3 — violet
  "oklch(0.55 0.15 200)", // GPU 4 — teal
  "oklch(0.6 0.15 20)", // GPU 5 — rose
  "oklch(0.55 0.12 140)", // GPU 6 — green-blue
  "oklch(0.6 0.15 55)", // GPU 7 — orange
];

function getGpuColor(index: number): string {
  return GPU_COLORS[index % GPU_COLORS.length] ?? "oklch(0.45 0.12 160)";
}

function formatTimestamp(timestamp: string, range: TimeRange): string {
  const date = new Date(timestamp);
  if (range === "1h" || range === "6h" || range === "24h") {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }
  return date.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function CustomTooltip(props: {
  active?: boolean;
  payload?: Array<{ dataKey?: string | number; name?: string; value?: number; color?: string }>;
  label?: string;
  range: TimeRange;
  unit: string;
  valueFormatter?: (value: number) => string;
}) {
  const { active, payload, label, range, unit, valueFormatter } = props;
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-popover-foreground shadow-md">
      <p className="mb-1.5 font-mono text-[11px] text-muted-foreground">
        {formatTimestamp(label as string, range)}
      </p>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="flex items-center gap-2 text-[12px]">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-muted-foreground">{entry.name}:</span>
          <span className="font-mono font-medium">
            {valueFormatter
              ? valueFormatter(entry.value as number)
              : (entry.value as number).toFixed(1)}
            {unit}
          </span>
        </div>
      ))}
    </div>
  );
}

interface ChartSeries {
  dataKey: string;
  name: string;
  color: string;
}

interface BaseChartProps {
  data: Record<string, unknown>[];
  series: ChartSeries[];
  range: TimeRange;
  unit: string;
  yDomain?: [number, number];
  valueFormatter?: (value: number) => string;
  allowDecimals?: boolean;
}

function NoData() {
  return (
    <div className="flex h-[250px] items-center justify-center">
      <span className="text-[13px] text-muted-foreground">No data</span>
    </div>
  );
}

export function AreaMetricsChart({
  data,
  series,
  range,
  unit,
  yDomain,
  valueFormatter,
  allowDecimals = true,
}: BaseChartProps) {
  if (data.length === 0) return <NoData />;

  return (
    <ResponsiveContainer width="100%" height={250}>
      <AreaChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
        <defs>
          {series.map((seriesItem) => (
            <linearGradient
              key={seriesItem.dataKey}
              id={`gradient-${seriesItem.dataKey}`}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop offset="0%" stopColor={seriesItem.color} stopOpacity={0.3} />
              <stop offset="100%" stopColor={seriesItem.color} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" strokeOpacity={0.5} />
        <XAxis
          dataKey="timestamp"
          tickFormatter={(timestamp: string) => formatTimestamp(timestamp, range)}
          stroke="var(--color-muted-foreground)"
          tick={{ fontSize: 11, fontFamily: "var(--font-mono)" }}
          tickLine={false}
          axisLine={false}
          minTickGap={40}
        />
        <YAxis
          domain={yDomain ?? ["auto", "auto"]}
          allowDecimals={allowDecimals}
          stroke="var(--color-muted-foreground)"
          tick={{ fontSize: 11, fontFamily: "var(--font-mono)" }}
          tickLine={false}
          axisLine={false}
          tickFormatter={(value: number) =>
            valueFormatter ? valueFormatter(value) : `${value}${unit}`
          }
          width={50}
        />
        <Tooltip
          content={<CustomTooltip range={range} unit={unit} valueFormatter={valueFormatter} />}
        />
        {series.length > 1 && (
          <Legend wrapperStyle={{ fontSize: "11px", fontFamily: "var(--font-mono)" }} />
        )}
        {series.map((seriesItem) => (
          <Area
            key={seriesItem.dataKey}
            type="monotone"
            dataKey={seriesItem.dataKey}
            name={seriesItem.name}
            stroke={seriesItem.color}
            fill={`url(#gradient-${seriesItem.dataKey})`}
            strokeWidth={1.5}
            dot={false}
            activeDot={{ r: 3, strokeWidth: 0 }}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function LineMetricsChart({
  data,
  series,
  range,
  unit,
  yDomain,
  valueFormatter,
  allowDecimals = true,
}: BaseChartProps) {
  if (data.length === 0) return <NoData />;

  return (
    <ResponsiveContainer width="100%" height={250}>
      <LineChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" strokeOpacity={0.5} />
        <XAxis
          dataKey="timestamp"
          tickFormatter={(timestamp: string) => formatTimestamp(timestamp, range)}
          stroke="var(--color-muted-foreground)"
          tick={{ fontSize: 11, fontFamily: "var(--font-mono)" }}
          tickLine={false}
          axisLine={false}
          minTickGap={40}
        />
        <YAxis
          domain={yDomain ?? ["auto", "auto"]}
          allowDecimals={allowDecimals}
          stroke="var(--color-muted-foreground)"
          tick={{ fontSize: 11, fontFamily: "var(--font-mono)" }}
          tickLine={false}
          axisLine={false}
          tickFormatter={(value: number) =>
            valueFormatter ? valueFormatter(value) : `${value}${unit}`
          }
          width={50}
        />
        <Tooltip
          content={<CustomTooltip range={range} unit={unit} valueFormatter={valueFormatter} />}
        />
        {series.length > 1 && (
          <Legend wrapperStyle={{ fontSize: "11px", fontFamily: "var(--font-mono)" }} />
        )}
        {series.map((seriesItem) => (
          <Line
            key={seriesItem.dataKey}
            type="monotone"
            dataKey={seriesItem.dataKey}
            name={seriesItem.name}
            stroke={seriesItem.color}
            strokeWidth={1.5}
            dot={false}
            activeDot={{ r: 3, strokeWidth: 0 }}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

export { formatTimestamp, getGpuColor };
